import React, { useState, useEffect, useRef } from 'react';
import { MdCheckCircle, MdMoreVert, MdVolumeOff, MdVolumeUp } from 'react-icons/md';
import Hls from 'hls.js';
import { getTranslation } from './translations';
import { recordWatchHistoryItem } from './HistoryPage';
import { getAvatarColor } from './utils/avatarUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './VideoCard.css';

export const DEFAULT_VIDEO_THUMBNAIL = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='640' height='360' viewBox='0 0 640 360'%3E%3Crect width='640' height='360' fill='%231f1f1f'/%3E%3Cpath d='M280 140 L380 180 L280 220 Z' fill='%23606060'/%3E%3C/svg%3E";

// Global cache for video preview streams to prevent redundant network fetches
const streamUrlCache = new Map();
const pendingRequests = new Map();

async function fetchVideoStreamUrl(vidId) {
  if (!vidId) return null;
  const key = String(vidId);
  if (streamUrlCache.has(key)) {
    return streamUrlCache.get(key);
  }
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const promise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/videos/${vidId}`, {
        headers: {
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          devicetype: '0',
          request_type: 'USER',
        },
      });
      if (res.ok) {
        const json = await res.json();
        const vid = json?.data?.video;
        if (vid) {
          // Prefer video_144_url (fastest direct MP4 buffering for hover previews)
          let stream = (vid.video_144_url && vid.video_144_url.includes('.'))
            ? vid.video_144_url
            : vid.video_url;
          if (stream) {
            const normalized = normalizeMediaUrl(stream);
            streamUrlCache.set(key, normalized);
            if (vid.id) streamUrlCache.set(String(vid.id), normalized);
            if (vid.video_url_id) streamUrlCache.set(String(vid.video_url_id), normalized);
            return normalized;
          }
        }
      }
    } catch (e) {
      console.warn('Error fetching hover preview stream for video:', vidId, e);
    } finally {
      pendingRequests.delete(key);
    }
    return null;
  })();

  pendingRequests.set(key, promise);
  return promise;
}

function parseDurationToSeconds(str) {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const parts = String(str).trim().split(':').map(Number);
  if (parts.some(isNaN)) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function formatScrubTime(seconds) {
  if (isNaN(seconds) || seconds < 0) return '0:00';
  const totalSecs = Math.floor(seconds);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}:${remMins < 10 ? '0' : ''}${remMins}:${secs < 10 ? '0' : ''}${secs}`;
  }
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
}

export default function VideoCard({ video, onVideoClick, onChannelClick }) {
  const [, setLangTick] = useState(0);

  // Hover-to-Play Video Preview States
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [streamUrl, setStreamUrl] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [parsedDuration, setParsedDuration] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubHoverPercent, setScrubHoverPercent] = useState(null);

  const hoverTimerRef = useRef(null);
  const isHoveringRef = useRef(false);
  const isMountedRef = useRef(true);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const progressContainerRef = useRef(null);
  const wasScrubbingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    const handleLangChange = () => setLangTick((t) => t + 1);
    window.addEventListener('speedotube_language_changed', handleLangChange);
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('speedotube_language_changed', handleLangChange);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, []);

  const {
    title,
    channelName,
    channelAvatar,
    verified,
    views,
    timestamp,
    duration,
    thumbnailUrl,
  } = video;

  const isVerified = verified !== undefined ? Boolean(verified) : true;

  // Hover detection with 320ms debounce (matching YouTube desktop behavior)
  const handleMouseEnter = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    isHoveringRef.current = true;

    hoverTimerRef.current = setTimeout(async () => {
      if (!isHoveringRef.current || !isMountedRef.current) return;
      setIsHovering(true);

      const vidId = video.videoUrlId || video.id;
      let url = video.streamUrl || video.video_144_url || video.video_url || video.mp4Url;
      if (!url) {
        url = await fetchVideoStreamUrl(vidId);
      }
      if (isMountedRef.current && isHoveringRef.current && url) {
        setStreamUrl(url);
      }
    }, 320);
  };

  const handleMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    isHoveringRef.current = false;
    setIsHovering(false);
    setIsPlaying(false);
    setIsScrubbing(false);
    setScrubHoverPercent(null);
    setCurrentTime(0);
    setStreamUrl(null);

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.removeAttribute('src');
        videoRef.current.load();
      } catch (e) {}
    }
  };

  // Attach video stream when streamUrl and isHovering are active
  useEffect(() => {
    if (!streamUrl || !videoRef.current || !isHovering) return;
    const videoEl = videoRef.current;
    videoEl.muted = isMuted;

    const isHls = typeof streamUrl === 'string' && streamUrl.includes('.m3u8');
    if (isHls && Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        capLevelToPlayerSize: true,
      });
      hls.loadSource(streamUrl);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (isHoveringRef.current && isMountedRef.current) {
          videoEl.play().catch(() => {});
        }
      });
      hlsRef.current = hls;
    } else {
      videoEl.src = streamUrl;
      videoEl.play().catch(() => {});
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [streamUrl, isHovering]);

  // Sync mute state on video element
  const handleToggleMute = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current && videoRef.current.duration && !isNaN(videoRef.current.duration)) {
      setParsedDuration(videoRef.current.duration);
    }
  };

  // Progress line percent
  const effectiveDuration = parsedDuration || (video.rawDuration ? Number(video.rawDuration) : 0) || parseDurationToSeconds(duration);
  const progressPercent = effectiveDuration > 0
    ? Math.min(100, Math.max(0, (currentTime / effectiveDuration) * 100))
    : 0;

  // Interactive scrubber handlers (YouTube style)
  const calculateScrubPercent = (clientX) => {
    if (!progressContainerRef.current) return 0;
    const rect = progressContainerRef.current.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const pos = (clientX - rect.left) / rect.width;
    return Math.min(100, Math.max(0, pos * 100));
  };

  const handleScrubberMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!videoRef.current || effectiveDuration <= 0) return;

    setIsScrubbing(true);
    wasScrubbingRef.current = true;
    const pct = calculateScrubPercent(e.clientX);
    const newTime = (pct / 100) * effectiveDuration;
    try {
      videoRef.current.currentTime = newTime;
    } catch (err) {}
    setCurrentTime(newTime);

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const movePct = calculateScrubPercent(moveEvent.clientX);
      setScrubHoverPercent(movePct);
      if (videoRef.current && effectiveDuration > 0) {
        const t = (movePct / 100) * effectiveDuration;
        try {
          videoRef.current.currentTime = t;
        } catch (err) {}
        setCurrentTime(t);
      }
    };

    const handleMouseUp = (upEvent) => {
      if (upEvent) upEvent.preventDefault();
      setIsScrubbing(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      setTimeout(() => {
        wasScrubbingRef.current = false;
      }, 150);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleScrubberMouseMove = (e) => {
    e.stopPropagation();
    const pct = calculateScrubPercent(e.clientX);
    setScrubHoverPercent(pct);
  };

  const handleScrubberMouseLeave = () => {
    if (!isScrubbing) {
      setScrubHoverPercent(null);
    }
  };

  const handleScrubberClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!videoRef.current || effectiveDuration <= 0) return;
    const pct = calculateScrubPercent(e.clientX);
    const newTime = (pct / 100) * effectiveDuration;
    try {
      videoRef.current.currentTime = newTime;
    } catch (err) {}
    setCurrentTime(newTime);
  };

  const handleChannelClick = (e) => {
    e.stopPropagation();
    const handle = video.handleName || video.channelHandle || video.channelName || '';
    if (!handle) return;
    const cleanHandle = handle.startsWith('@') ? handle : `@${handle}`;
    if (onChannelClick) {
      onChannelClick(cleanHandle);
    } else {
      const channelPath = `/channel/${encodeURIComponent(cleanHandle)}`;
      window.dispatchEvent(
        new CustomEvent('speedotube_navigate', {
          detail: { path: channelPath },
        })
      );
    }
  };

  const handleCardClick = (e) => {
    // If clicking menu button, mute button, scrubber bar, or channel links, don't navigate to video
    if (
      wasScrubbingRef.current ||
      isScrubbing ||
      e.target.closest('.yt-card-menu-btn') ||
      e.target.closest('.yt-preview-mute-btn') ||
      e.target.closest('.yt-preview-progress-container') ||
      e.target.closest('.yt-preview-progress-track') ||
      e.target.closest('.yt-channel-avatar') ||
      e.target.closest('.yt-channel-name')
    ) {
      return;
    }

    recordWatchHistoryItem(video);
    if (onVideoClick) {
      onVideoClick(video);
    } else {
      const vidId = video.videoUrlId || video.id;
      const watchPath = `/watch?v=${vidId}`;
      window.dispatchEvent(
        new CustomEvent('speedotube_navigate', {
          detail: { path: watchPath, videoId: vidId },
        })
      );
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    let normalized = String(ts)
      .replace(/\b1\s+days\s+ago\b/gi, '1 day ago')
      .replace(/\b1\s+hours\s+ago\b/gi, '1 hour ago')
      .replace(/\b1\s+weeks\s+ago\b/gi, '1 week ago')
      .replace(/\b1\s+months\s+ago\b/gi, '1 month ago')
      .replace(/\b1\s+years\s+ago\b/gi, '1 year ago');

    if (normalized.includes('days ago')) return normalized.replace('days ago', getTranslation('daysAgo') || 'days ago');
    if (normalized.includes('day ago')) return normalized.replace('day ago', getTranslation('dayAgo') || 'day ago');
    if (normalized.includes('hours ago')) return normalized.replace('hours ago', getTranslation('hoursAgo') || 'hours ago');
    if (normalized.includes('hour ago')) return normalized.replace('hour ago', getTranslation('hourAgo') || 'hour ago');
    if (normalized.includes('weeks ago')) return normalized.replace('weeks ago', getTranslation('weeksAgo') || 'weeks ago');
    if (normalized.includes('week ago')) return normalized.replace('week ago', getTranslation('weekAgo') || 'week ago');
    if (normalized.includes('months ago')) return normalized.replace('months ago', getTranslation('monthsAgo') || 'months ago');
    if (normalized.includes('month ago')) return normalized.replace('month ago', getTranslation('monthAgo') || 'month ago');
    if (normalized.includes('Streamed live')) return getTranslation('streamedLive') || 'Streamed live';
    return normalized;
  };

  return (
    <div
      className={`yt-video-card ${isHovering ? 'yt-video-card-hovered' : ''}`}
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: 'pointer' }}
    >
      {/* Thumbnail section */}
      <div className="yt-thumbnail-wrapper">
        <img
          src={thumbnailUrl || DEFAULT_VIDEO_THUMBNAIL}
          alt={title}
          className={`yt-thumbnail ${isPlaying ? 'yt-thumbnail-dimmed' : ''}`}
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = DEFAULT_VIDEO_THUMBNAIL;
          }}
        />

        {/* Video Preview Overlay */}
        {isHovering && streamUrl && (
          <div className="yt-preview-player-container">
            <video
              ref={videoRef}
              className={`yt-preview-video ${isPlaying ? 'yt-preview-video-active' : ''}`}
              playsInline
              autoPlay
              muted={isMuted}
              loop
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlaying={() => setIsPlaying(true)}
              onError={(e) => {
                console.warn('Hover preview playback error:', e);
                setIsPlaying(false);
              }}
            />
          </div>
        )}

        {/* Floating Mute / Unmute Button */}
        {isHovering && (isPlaying || streamUrl) && (
          <button
            type="button"
            className="yt-preview-mute-btn"
            onClick={handleToggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MdVolumeOff size={16} /> : <MdVolumeUp size={16} />}
          </button>
        )}

        {/* YouTube-style Controllable Red Video Progress Scrubber Bar */}
        {isHovering && (isPlaying || streamUrl || currentTime > 0) && (
          <div
            ref={progressContainerRef}
            className={`yt-preview-progress-container ${isScrubbing ? 'yt-is-scrubbing' : ''}`}
            onMouseDown={handleScrubberMouseDown}
            onMouseMove={handleScrubberMouseMove}
            onMouseLeave={handleScrubberMouseLeave}
            onClick={handleScrubberClick}
            title="Click or drag to seek"
          >
            {/* Timestamp Tooltip on hover */}
            {scrubHoverPercent !== null && effectiveDuration > 0 && (
              <div
                className="yt-preview-progress-tooltip"
                style={{
                  left: `${Math.min(92, Math.max(8, scrubHoverPercent))}%`,
                }}
              >
                {formatScrubTime((scrubHoverPercent / 100) * effectiveDuration)}
              </div>
            )}

            <div className="yt-preview-progress-track">
              {/* Ghost hover bar showing target position */}
              {scrubHoverPercent !== null && (
                <div
                  className="yt-preview-progress-hover"
                  style={{ width: `${scrubHoverPercent}%` }}
                />
              )}
              {/* Active red progress fill */}
              <div
                className="yt-preview-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
              {/* Red scrubber handle thumb */}
              <div
                className="yt-preview-progress-thumb"
                style={{ left: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Duration badge - hidden during active video playback */}
        <span className={`yt-duration-badge ${isPlaying ? 'yt-duration-badge-hidden' : ''}`}>
          {duration}
        </span>
      </div>

      {/* Video details section */}
      <div className="yt-card-details">
        <div 
          className="yt-channel-avatar"
          onClick={handleChannelClick}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          title={`Go to ${channelName || 'channel'}`}
        >
          {channelAvatar ? (
            <img
              src={channelAvatar}
              alt={channelName}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextElementSibling) {
                  e.currentTarget.nextElementSibling.style.display = 'flex';
                }
              }}
            />
          ) : null}
          <div
            className="yt-avatar-fallback"
            style={{
              backgroundColor: getAvatarColor(channelName),
              display: channelAvatar ? 'none' : 'flex',
            }}
          >
            {(channelName || 'P').charAt(0).toUpperCase()}
          </div>
        </div>

        <div className="yt-card-info">
          <h3 className="yt-video-title" title={title}>
            {title}
          </h3>
          
          <div 
            className="yt-channel-name"
            onClick={handleChannelClick}
            role="button"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            title={`Go to ${channelName || 'channel'}`}
          >
            <span>{channelName}</span>
            {isVerified && (
              <MdCheckCircle
                className="yt-verified-icon"
                size={14}
                title="Verified"
                aria-label="Verified"
              />
            )}
          </div>

          <div className="yt-video-meta">
            <span>{views} {getTranslation('views')}</span>
            <span className="yt-bullet">•</span>
            <span>{formatTimestamp(timestamp)}</span>
          </div>
        </div>

        <button className="yt-card-menu-btn" title="More options">
          <MdMoreVert size={20} />
        </button>
      </div>
    </div>
  );
}
